import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Spinner,
  Card,
  Modal,
  Row,
  Col,
  Pagination,
  Badge,
  InputGroup,
  Form,
} from "react-bootstrap";
import {
  Eye,
  Telephone,
  Envelope,
  Calendar,
  Search,
  ArrowClockwise,
} from "react-bootstrap-icons";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://34.206.193.218:4001/api/admin";

const ContractorManagement = () => {
  const [contractors, setContractors] = useState([]);
  const [filteredContractors, setFilteredContractors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedContractor, setSelectedContractor] = useState(null);
  const [showView, setShowView] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  /* ================= FETCH CONTRACTORS ================= */
  const fetchContractors = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/contractors/getAll`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: currentPage, limit },
      });

      if (res.data.success) {
        setContractors(res.data.data || []);
        setFilteredContractors(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch {
      toast.error("Failed to load contractors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContractors();
  }, [currentPage]);

  /* ================= SEARCH ================= */
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredContractors(contractors);
      return;
    }

    const lower = searchTerm.toLowerCase();

    const filtered = contractors.filter(
      (c) =>
        c.subcontractorName?.toLowerCase().includes(lower) ||
        c.email?.toLowerCase().includes(lower) ||
        c.phone?.toLowerCase().includes(lower)
    );

    setFilteredContractors(filtered);
  }, [searchTerm, contractors]);

  /* ================= PAGINATION ================= */
  const renderPagination = () => {
    const items = [];
    for (let i = 1; i <= totalPages; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  };

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ===== HEADER ===== */}
      <Card className="border-0 mb-4">
        <Card.Body className="p-0">
          <Row className="align-items-center g-3 justify-content-between">
            {/* TITLE */}
            <Col md={6}>
              <h4 className="fw-bold mb-0">Sub Contractor Management</h4>
              <small className="text-muted">
                View subcontractors and their assigned projects
              </small>
            </Col>

            {/* SEARCH + REFRESH */}
            <Col md={6}>
              <div className="d-flex">
                <InputGroup>
                  <InputGroup.Text>
                    <Search />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by name, email or phone"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </InputGroup>

                <Button
                  variant="outline-primary"
                  className="ms-2"
                  title="Refresh"
                  onClick={() => {
                    setSearchTerm("");
                    setCurrentPage(1);
                    fetchContractors();
                  }}
                >
                  <ArrowClockwise />
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ===== TABLE ===== */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Sub Contractor Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Compliance</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContractors.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">
                          No contractors found
                        </td>
                      </tr>
                    ) : (
                      filteredContractors.map((c) => (
                        <tr key={c._id}>
                          <td className="fw-semibold">
                            {c.subcontractorName}
                          </td>
                          <td>
                            <Envelope className="me-1" />
                            {c.email}
                          </td>
                          <td>
                            <Telephone className="me-1" />
                            {c.phone || "-"}
                          </td>
                          <td>
                            <Badge
                              bg={c.agreementSigned ? "success" : "secondary"}
                              className="me-1"
                            >
                              Agreement
                            </Badge>
                            <Badge
                              bg={
                                c.liabilityInsurance
                                  ? "success"
                                  : "secondary"
                              }
                            >
                              Insurance
                            </Badge>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => {
                                setSelectedContractor(c);
                                setShowView(true);
                              }}
                            >
                              <Eye />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="p-3 d-flex justify-content-end">
                  <Pagination className="mb-0">
                    {renderPagination()}
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* ===== VIEW MODAL ===== */}
      <Modal show={showView} onHide={() => setShowView(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Contractor Details – {selectedContractor?.subcontractorName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedContractor && (
            <>
              <Row className="mb-4">
                <Col md={4}>
                  <p>
                    <Envelope className="me-2" />
                    {selectedContractor.email}
                  </p>
                  <p>
                    <Telephone className="me-2" />
                    {selectedContractor.phone || "N/A"}
                  </p>
                </Col>
                <Col md={8}>
                  <Badge
                    bg={
                      selectedContractor.workersCompensation
                        ? "success"
                        : "secondary"
                    }
                    className="me-2"
                  >
                    Workers Compensation
                  </Badge>
                  <Badge
                    bg={
                      selectedContractor.liabilityInsurance
                        ? "success"
                        : "secondary"
                    }
                    className="me-2"
                  >
                    Liability Insurance
                  </Badge>
                  <Badge
                    bg={
                      selectedContractor.agreementSigned
                        ? "success"
                        : "secondary"
                    }
                  >
                    Agreement Signed
                  </Badge>
                </Col>
              </Row>

              {selectedContractor.projects?.length > 0 ? (
                selectedContractor.projects.map((project, pIdx) => (
                  <Card key={pIdx} className="mb-3">
                    <Card.Body>
                      <h6 className="fw-bold mb-3">
                        Project {project.projectName || ""}
                      </h6>

                      {project.tasks?.length > 0 ? (
                        <Table bordered size="sm">
                          <thead>
                            <tr>
                              <th>Scheduled Date</th>
                              <th>Installed Date</th>
                              <th>Notes</th>
                              <th>Rate / Sqft</th>
                              <th>Total Sqft</th>
                              <th>Total Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {project.tasks.map((task, tIdx) => (
                              <tr key={tIdx}>
                                <td>
                                  <Calendar className="me-1" />
                                  {task.scheduledDate
                                    ? new Date(
                                        task.scheduledDate
                                      ).toLocaleDateString()
                                    : "-"}
                                </td>
                                <td>
                                  {task.installedDate
                                    ? new Date(
                                        task.installedDate
                                      ).toLocaleDateString()
                                    : "-"}
                                </td>
                                <td>{task.notes || "-"}</td>
                                <td>{task.ratePerSqft || "-"}</td>
                                <td>{task.totalSqft || "-"}</td>
                                <td>₹{task.totalAmount || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="text-muted mb-0">No tasks assigned</p>
                      )}
                    </Card.Body>
                  </Card>
                ))
              ) : (
                <p className="text-muted">No projects assigned</p>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ContractorManagement;
